import React, { Fragment, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Card, Button } from "react-bootstrap";
import PropTypes from "prop-types";

import Alert from "components/molecules/Alert/Alert";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import { useGetExceptions } from "api/queries/shipments/useGetExceptions";

const ExceptionsWidget = function ({ isLayoutLocked, isPreview, paymentLocation, widgetId, handleWidgetDialogOpen }) {
  const intl = useIntl();
  const navigate = useNavigate();

  const [error, setError] = useState(false);

  const { data: exceptions, isFetching } = useGetExceptions({
    onError: (err) => {
      setError(true);
      errorToast(
        err,
        intl.formatMessage({
          id: "dashboard.exceptionsWidget.couldNotRetrieveException",
          defaultMessage: "Could not retrieve exceptions. Please try again later.",
        }),
      );
    },
  });

  function generateUrl(type, reference) {
    let path = "";

    switch (type) {
      case "quote_request":
        path = `/shipment/${reference}`;
        break;
      case "shipment":
        path = `/shipment/${reference}/tracking`;
        break;
      default:
        path = `/shipment/${reference}`;
    }

    return path;
  }

  if (isFetching) {
    return (
      <div className="overflow-hidden">
        <LoadingSkeleton
          header={intl.formatMessage({
            id: "general.exceptions",
            defaultMessage: "Exceptions",
          })}
          rows={isPreview ? 2 : 6}
          className="pl-16 pr-16 pt-1"
        />
      </div>
    );
  }
  if (error) {
    return (
      <Alert className="mb-0" variant="danger">
        <FormattedMessage
          id="dashboard.couldNotRetrieveUpdates"
          defaultMessage="We could not retrieve your updates. Please try again."
        />
      </Alert>
    );
  }
  return (
    <Card.Body className="overflow-auto">
      <div
        className={`d-flex justify-content-${
          isLayoutLocked ? "center" : "between"
        } align-items-center mb-2 pb-2 br-5 bg-white rounded-bottom-left-0 rounded-bottom-right-0`}
      >
        <h5 className="pl-1">
          <FormattedMessage id="general.exceptions" defaultMessage="Exceptions" />
          {paymentLocation && paymentLocation !== "All" && (
            <span className="font-b4 text-secondary ml-2">({paymentLocation})</span>
          )}
        </h5>
        {!isLayoutLocked && !isPreview && (
          <Button
            className="edit-btn"
            onClick={() => {
              handleWidgetDialogOpen(widgetId);
            }}
            data-testid={`${testIds.EDIT_WIDGET_BUTTON}-${widgetId}`}
          >
            <FormattedMessage id="general.edit" defaultMessage="Edit" />
          </Button>
        )}
      </div>
      {!exceptions.length && (
        <div className="h-100 d-flex justify-content-center align-items-center">
          <span className="text-secondary" style={{ transform: isPreview ? "" : "translateY(-30px)" }}>
            <FormattedMessage id="dashboard.exceptionsWidget.noExceptionsFound" defaultMessage="You have no exceptions" />
          </span>
        </div>
      )}
      <ul className="row flex-column list-widget-container" style={isPreview ? { maxHeight: 300, overflow: "auto" } : {}}>
        {exceptions.map(({ shipment_id, shipment_title, exception_display }, i) => (
          <Fragment key={shipment_id + (shipment_title || i)}>
            <li
              className={`col list-widget-item ${!isLayoutLocked || isPreview ? "previewing" : ""} ${
                i === exceptions.length - 1 ? "" : "border-bottom border-grey"
              }`}
            >
              <div
                aria-hidden
                onClick={() => {
                  if (isLayoutLocked && !isPreview) {
                    navigate(generateUrl("shipment", shipment_id));
                  }
                }}
                className="d-flex row text-primary text-decoration-none pt-4 pb-4"
              >
                <Col className="d-flex flex-column align-items-start justify-content-start">
                  <span className="text-primary font-weight-bold d-block font-b4">
                    {shipment_id} {shipment_title && "|"} {shipment_title}
                  </span>

                  {exception_display ? (
                    <p className="font-b3">{exception_display}</p>
                  ) : (
                    <p className="text-secondary font-b3">
                      {" "}
                      <FormattedMessage
                        id="dashboard.exceptionsWidget.noInformationAvailable"
                        defaultMessage=" No information available"
                      />
                    </p>
                  )}
                </Col>
              </div>
            </li>
            <hr className="list-widget-line" />
          </Fragment>
        ))}
      </ul>
    </Card.Body>
  );
};

ExceptionsWidget.propTypes = {
  paymentLocation: PropTypes.string,
  isLayoutLocked: PropTypes.bool,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

ExceptionsWidget.defaultProps = {
  isLayoutLocked: false,
  paymentLocation: null,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default ExceptionsWidget;
